import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { requests } from "../../../common/RestAPI";
import { base64Encoding, generateQueryParam, getTimestampToDate } from "../../../common/util";
import { SESSIONSTORAGE } from "../../../value/staticValues";
import { PAGE_URL, PLATFORM_URL } from "../../../value/Url";

import ContentsProps from "../../../interfaceTypes/ContentsProps";

import { ListFilter } from "../../base/table/listTableType";
import { actionMenus, categoryFilters, tableHeaders, vmAddModalContent, VmData } from "./VmData";
import { PopupModalItemType } from "../../base/modal/PopupModal";

import VmMgmt from "./VmMgmt";
import { ALERTS, POPUP_TYPE } from "../../../value/type";
import { VmCommandEmptyPackage, VmParse } from "../../../interfaceTypes/VmType";

interface VmMgmtContainerProps extends ContentsProps {
    moveDetailCb: Function;
}

export default function VmMgmtContainer(props:VmMgmtContainerProps) {
    const { t } = useTranslation();
    const [infoText, setInfoText] = useState('');
    const [infoError, setInfoError] = useState(false);

    const fetchIdRef = useRef(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [totalItemCount, setTotalItemCount] = useState(0);
    const [categoryFilterItems, setCategoryFilterItems] = useState<ListFilter[]>(categoryFilters);
    const [selectItems, setSelectItems] = useState([]);
    const [tableData, setTableData] = useState<VmData[]>([]);
    const [refreshYn, setRefreshYn] = useState(false);

    const [executeCommandStartVmYn, setExecuteCommandStartVmYn] = useState(false);
    const [executeCommandStopVmYn, setExecuteCommandStopVmYn] = useState(false);
    const [executeCommandPauseVmYn, setExecuteCommandPauseVmYn] = useState(false);
    const [executeCommandResumeVmYn, setExecuteCommandResumeVmYn] = useState(false);
    const [executeCommandRebootVmYn, setExecuteCommandRebootVmYn] = useState(false);
    const [executeCommandMigrationVmYn, setExecuteCommandMigrationVmYn] = useState(false);


    const [executeDeleteVmYn, setExecuteDeleteVmYn] = useState(false);
    const [actionResultTitle, setActionResultTitle] = useState("");
    const [actionResultMessage, setActionResultMessage] = useState("");
    const [actionResults, setActionResults] = useState<PopupModalItemType[]>([]);
    const [actionResultCount, setActionResultCount] = useState(0);


    const headers = useMemo(() => tableHeaders, []);
    const navigate = useNavigate();





    useEffect(() => {
        AlertFunction('');
    }, []);

    function AlertFunction(text:string, error=false) {
        setInfoText(text);
        setInfoError(error);
    }

    useEffect(() => {
        if(actionResults.length === 0) {
            return ;
        }
        if(actionResultMessage && actionResultTitle) {
            const items = actionResults.map((item, index) => {
                return { target: item.target, successYn: item.successYn, reason: item.reason}
            });
            const completeYn = items.length === selectItems.length ? true : false;
            props.showPopupModalCb({
                openYn: true,
                type: POPUP_TYPE.LIST_MESSAGE_COMPLETE_POPUP,
                title: actionResultTitle,
                message: actionResultMessage,
                items: items,
                completeTargetText: t("VM_NAME_TEXT"),
                code: "COMPLETE",
                cancelButtonYn: false,
                confirmButtonCb: confirmAction,
                completeYn: completeYn
            });

            if(completeYn) {
                props.setLoading(false);
            }
        }
        if(actionResults.length > 0) {
            setActionResultCount(actionResultCount - 1);
        }
    }, [actionResults, actionResultTitle, actionResultMessage]);


    useEffect(() => {
        if(actionResultCount === selectItems.length) {
            props.setLoading(true);
        }
        if(actionResultCount > 0) {
            if(executeDeleteVmYn) {
                setActionResultTitle(t("VM_DELETE_COMPLETE_TEXT"));
                setActionResultMessage(t("MESSAGE_VM_DELETE_COMPLETE_MESSAGE"));
                executeDeleteVm(selectItems[selectItems.length - actionResultCount]);
            } else if(executeCommandStartVmYn) {
                setActionResultTitle(t("VM_START_COMPLETE_TEXT"));
                setActionResultMessage(t("MESSAGE_VM_START_COMPLETE_MESSAGE"));
                executeStartVm(selectItems[selectItems.length - actionResultCount]);
            } else if(executeCommandStopVmYn) {
                setActionResultTitle(t("VM_STOP_COMPLETE_TEXT"));
                setActionResultMessage(t("MESSAGE_VM_STOP_COMPLETE_MESSAGE"));
                executeStopVm(selectItems[selectItems.length - actionResultCount]);
            } else if(executeCommandPauseVmYn) {
                setActionResultTitle(t("VM_PAUSE_COMPLETE_TEXT"));
                setActionResultMessage(t("MESSAGE_VM_PAUSE_COMPLETE_MESSAGE"));
                executePauseVm(selectItems[selectItems.length - actionResultCount]);
            } else if(executeCommandResumeVmYn) {
                setActionResultTitle(t("VM_RESUME_COMPLETE_TEXT"));
                setActionResultMessage(t("MESSAGE_VM_RESUME_COMPLETE_MESSAGE"));
                executeResumeVm(selectItems[selectItems.length - actionResultCount]);
            } else if(executeCommandRebootVmYn) {
                setActionResultTitle(t("VM_REBOOT_COMPLETE_TEXT"));
                setActionResultMessage(t("MESSAGE_VM_REBOOT_COMPLETE_MESSAGE"));
                executeRebootVm(selectItems[selectItems.length - actionResultCount]);
            } else if(executeCommandMigrationVmYn) {
                setActionResultTitle(t("VM_MIGRATION_COMPLETE_TEXT"));
                setActionResultMessage(t("MESSAGE_VM_MIGRATION_COMPLETE_MESSAGE"));
                executeMigrationVm(selectItems[selectItems.length - actionResultCount]);
            }
        }
    }, [actionResultCount, executeDeleteVmYn, executeCommandStartVmYn, executeCommandStopVmYn, executeCommandPauseVmYn,
        executeCommandResumeVmYn, executeCommandRebootVmYn, executeCommandMigrationVmYn]);


    function executeResult(target, successYn, reason:string="") {
        const results = actionResults.concat([{target: target, successYn: successYn, reason:reason }]);
        setActionResults(results);
    }


    function executeDeleteVm(item) {
        requests.delete(PLATFORM_URL.VM + "/" + item.vm_id)
        .then((response) => {
            executeResult(item.vm_name, true);
        })
        .catch((error) => {
            executeResult(item.vm_name, false);
        })
    }

    function executeStartVm(item) {
        const actionPackageData = {};
        requests.post(PLATFORM_URL.VM + "/" + item.vm_id + '/start', actionPackageData)
        .then((response) => {
            executeResult(item.vm_name, true);
        })
        .catch((error) => {
            executeResult(item.vm_name, false);
        })
    }

    function executeStopVm(item) {
        const actionPackageData = {};
        requests.post(PLATFORM_URL.VM + "/" + item.vm_id + '/stop', actionPackageData)
        .then((response) => {
            executeResult(item.vm_name, true);
        })
        .catch((error) => {
            executeResult(item.vm_name, false);
        })
    }


    function executePauseVm(item) {
        const actionPackageData = {};
        requests.post(PLATFORM_URL.VM + "/" + item.vm_id + '/pause', actionPackageData)
        .then((response) => {
            executeResult(item.vm_name, true);
        })
        .catch((error) => {
            executeResult(item.vm_name, false);
        })
    }

    function executeResumeVm(item) {
        const actionPackageData = {};
        requests.post(PLATFORM_URL.VM + "/" + item.vm_id + '/resume', actionPackageData)
        .then((response) => {
            executeResult(item.vm_name, true);
        })
        .catch((error) => {
            executeResult(item.vm_name, false);
        })
    }

    function executeRebootVm(item) {
        const actionPackageData = {};
        requests.post(PLATFORM_URL.VM + "/" + item.vm_id + '/reboot', actionPackageData)
        .then((response) => {
            executeResult(item.vm_name, true);
        })
        .catch((error) => {
            executeResult(item.vm_name, false);
        })
    }

    function executeMigrationVm(item) {
        const actionPackageData = {};
        requests.post(PLATFORM_URL.VM + "/" + item.vm_id + '/migration', actionPackageData)
        .then((response) => {
            executeResult(item.vm_name, true);
        })
        .catch((error) => {
            executeResult(item.vm_name, false);
        })
    }

    function addVmResult(data) {
        if(data.successYn) {
            AlertFunction(t("MESSAGE_VM_ADD_SUCCESS_MESSAGE"));
            setRefreshYn(true);
        }
    }

    function confirmAction(code) {
        if(code === "VM_DELETE") {
            setActionResultCount(selectItems.length);
            setExecuteDeleteVmYn(true);
        } else if(code === "ACTION_START") {
            setActionResultCount(selectItems.length);
            setExecuteCommandStartVmYn(true);
        } else if(code === "ACTION_STOP") {
            setActionResultCount(selectItems.length);
            setExecuteCommandStopVmYn(true);
        } else if(code === "ACTION_PAUSE") {
            setActionResultCount(selectItems.length);
            setExecuteCommandPauseVmYn(true);
        } else if(code === "ACTION_RESUME") {
            setActionResultCount(selectItems.length);
            setExecuteCommandResumeVmYn(true);
        } else if(code === "ACTION_REBOOT") {
            setActionResultCount(selectItems.length);
            setExecuteCommandRebootVmYn(true);
        }else if(code === "ACTION_MIGRATION") {
            setActionResultCount(selectItems.length);
            setExecuteCommandMigrationVmYn(true);
        } else if(code !== "COMPLETE") {
            setActionResultCount(selectItems.length);
        } else {
            setActionResultCount(0);
            setActionResults([]);
            setActionResultMessage("");
            setActionResultTitle("");
            setExecuteDeleteVmYn(false);
            setExecuteCommandStartVmYn(false);
            setExecuteCommandStopVmYn(false);
            setExecuteCommandPauseVmYn(false);
            setExecuteCommandResumeVmYn(false);
            setExecuteCommandRebootVmYn(false);
            setExecuteCommandMigrationVmYn(false);
            setRefreshYn(true);
            AlertFunction('');
        }
    }

    function clickActionMenu(code) {
        if(code === "VM_ADD") {
            props.showSlideModalCb({...vmAddModalContent, openYn: true, resultCb: addVmResult});
        } else if(code === "VM_DELETE") {
            props.showPopupModalCb({openYn: true,
                                    type: POPUP_TYPE.LIST_MESSAGE_WITH_TITLE_POPUP,
                                    title: t("MESSAGE_VM_DELETE_REQUEST_TITLE_MESSAGE"),
                                    message: t("MESSAGE_VM_DELETE_REQUEST_MESSAGE"),
                                    items: selectItems.map((item: any, index) => {
                                            return { target: item.vm_name }
                                    }),
                                    code: "VM_DELETE",
                                    cancelButtonYn: true,
                                    confirmButtonCb: confirmAction,

            });
        } else if(code === "ACTION_START") {
            props.showPopupModalCb({openYn: true,
                                    type: POPUP_TYPE.LIST_MESSAGE_WITH_TITLE_POPUP,
                                    title: t("MESSAGE_VM_START_REQUEST_TITLE_MESSAGE"),
                                    message: t("MESSAGE_VM_START_REQUEST_MESSAGE"),
                                    items: selectItems.map((item: any, index) => {
                                            return { target: item.vm_name }
                                    }),
                                    code: "ACTION_START",
                                    cancelButtonYn: true,
                                    confirmButtonCb: confirmAction,

            });
        } else if(code === "ACTION_STOP") {
            props.showPopupModalCb({openYn: true,
                                    type: POPUP_TYPE.LIST_MESSAGE_WITH_TITLE_POPUP,
                                    title: t("MESSAGE_VM_STOP_REQUEST_TITLE_MESSAGE"),
                                    message: t("MESSAGE_VM_STOP_REQUEST_MESSAGE"),
                                    items: selectItems.map((item: any, index) => {
                                            return { target: item.vm_name }
                                    }),
                                    code: "ACTION_STOP",
                                    cancelButtonYn: true,
                                    confirmButtonCb: confirmAction,

            });
        } else if(code === "ACTION_PAUSE") {
            props.showPopupModalCb({openYn: true,
                                    type: POPUP_TYPE.LIST_MESSAGE_WITH_TITLE_POPUP,
                                    title: t("MESSAGE_VM_PAUSE_REQUEST_TITLE_MESSAGE"),
                                    message: t("MESSAGE_VM_PAUSE_REQUEST_MESSAGE"),
                                    items: selectItems.map((item: any, index) => {
                                            return { target: item.vm_name }
                                    }),
                                    code: "ACTION_PAUSE",
                                    cancelButtonYn: true,
                                    confirmButtonCb: confirmAction,

            });
        } else if(code === "ACTION_RESUME") {
            props.showPopupModalCb({openYn: true,
                                    type: POPUP_TYPE.LIST_MESSAGE_WITH_TITLE_POPUP,
                                    title: t("MESSAGE_VM_RESUME_REQUEST_TITLE_MESSAGE"),
                                    message: t("MESSAGE_VM_RESUME_REQUEST_MESSAGE"),
                                    items: selectItems.map((item: any, index) => {
                                            return { target: item.vm_name }
                                    }),
                                    code: "ACTION_RESUME",
                                    cancelButtonYn: true,
                                    confirmButtonCb: confirmAction,

            });
        } else if(code === "ACTION_REBOOT") {
            props.showPopupModalCb({openYn: true,
                                    type: POPUP_TYPE.LIST_MESSAGE_WITH_TITLE_POPUP,
                                    title: t("MESSAGE_VM_REBOOT_REQUEST_TITLE_MESSAGE"),
                                    message: t("MESSAGE_VM_REBOOT_REQUEST_MESSAGE"),
                                    items: selectItems.map((item: any, index) => {
                                            return { target: item.vm_name }
                                    }),
                                    code: "ACTION_REBOOT",
                                    cancelButtonYn: true,
                                    confirmButtonCb: confirmAction,

            });
        } else if(code === "ACTION_MIGRATION") {
            props.showPopupModalCb({openYn: true,
                                    type: POPUP_TYPE.LIST_MESSAGE_WITH_TITLE_POPUP,
                                    title: t("MESSAGE_VM_MIGRATION_REQUEST_TITLE_MESSAGE"),
                                    message: t("MESSAGE_VM_MIGRATION_REQUEST_MESSAGE"),
                                    items: selectItems.map((item: any, index) => {
                                            return { target: item.vm_name }
                                    }),
                                    code: "ACTION_MIGRATION",
                                    cancelButtonYn: true,
                                    confirmButtonCb: confirmAction,

            });
        } else if (code === 'VM_SYNC') {
            props.setLoading(true);

            requests.post(PLATFORM_URL.VM + PLATFORM_URL.SYNC, {})
            .then((response) => {
                if (response.status === 200) {
                    AlertFunction(t("SYNC_COMPLETED_TEXT"));
                }
                setRefreshYn(true);
                props.setLoading(false);
            })
            .catch((error) => {
                setRefreshYn(true);
                props.setLoading(false);
            })
        }
    }


    function selectRow(items) {
        setSelectItems(items);

        if(items.length > 0) {
            setActionMenuStatus(2, true);
            setActionMenuStatus(3, true);

        } else {
            setActionMenuStatus(2, false);
            setActionMenuStatus(3, false);
        }
    }


    function clickRow(item) {
        sessionStorage.removeItem(SESSIONSTORAGE.VM_TARGET_ID);
        props.moveDetailCb(item.vm_id);
        navigate(`${PAGE_URL.DETAIL_MANAGEMENT}`);
    }


    function executeLoadVmList(pageSize, pageIndex, sortByName, sortByDescYn, categoryType, categoryCode, search, showLoading) {
        if(showLoading) {
            setCurrentPage(pageIndex);
            props.setLoading(true);
        }
        requests.get(PLATFORM_URL.VM, generateQueryParam(pageSize, pageIndex, sortByName, sortByDescYn, categoryType, categoryCode, search))
        .then((response) => {
            if (response.status === 200 && response.data && pageIndex === response.data.page-1) {
                setTableData(response.data.items);
                setPageCount(response.data.pages);
                setTotalItemCount(response.data.total)
            }
            if(showLoading) {
                props.setLoading(false);
                setRefreshYn(false);
            }
        })
        .catch((_) => {
            AlertFunction(t("MESSAGE_CANNOT_LOAD_DATA_MESSAGE"), true);
            if(showLoading) {
                props.setLoading(false);
                setRefreshYn(false);
            }
        })
    }


    function setActionMenuStatus(id, activeYn) {
        actionMenus[id - 1].activeYn = activeYn;
    }


    const fetchData = useCallback(({ pageSize, pageIndex, sortByName, sortByDescYn, categoryType, categoryCode, search, showLoading }) => {
        const fetchId = ++fetchIdRef.current
        if(fetchId === fetchIdRef.current) {
            executeLoadVmList(pageSize, pageIndex, sortByName, sortByDescYn, categoryType, categoryCode, search, showLoading);
        }
    }, []);


    return (
        <VmMgmt
            categoryFilterItems={categoryFilterItems}
            clickActionMenu={clickActionMenu}
            selectRow={selectRow}
            clickRow={clickRow}
            fetchData={fetchData}
            currentPage={currentPage}
            pageCount={pageCount}
            headers={headers}
            tableData={tableData}
            totalItemCount={totalItemCount}
            refreshYn={refreshYn}
            infoText={infoText}
            infoError={infoError}
        />
    )
}